import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import './UserProfile.css';
import { clearPrivateOfflineData } from '../../api/offlineSession';

interface UserProfileProps {
  showFullProfile?: boolean;
}

const UserProfile: React.FC<UserProfileProps> = ({ showFullProfile = false }) => {
  const { state, dispatch } = useApp();
  // Click-toggled (not hover-only — most sessions are touch).
  const [menuOpen, setMenuOpen] = useState(false);
  const [pictureBusy, setPictureBusy] = useState(false);
  const [pictureError, setPictureError] = useState('');
  const compactProfileRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const firstMenuActionRef = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    if (!menuOpen) return undefined;

    firstMenuActionRef.current?.focus();
    const closeForEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      setMenuOpen(false);
      menuButtonRef.current?.focus();
    };
    const closeForOutsidePointer = (event: PointerEvent) => {
      if (!compactProfileRef.current?.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('keydown', closeForEscape);
    document.addEventListener('pointerdown', closeForOutsidePointer);
    return () => {
      document.removeEventListener('keydown', closeForEscape);
      document.removeEventListener('pointerdown', closeForOutsidePointer);
    };
  }, [menuOpen]);

  const uploadPicture = async (file?: File) => {
    if (!file || !state.user) return;
    setPictureBusy(true);
    setPictureError('');
    const body = new FormData();
    body.append('file', file);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL ?? 'http://localhost:8000'}/auth/me/profile-picture`,
        { method: 'POST', headers: token ? { Authorization: `Bearer ${token}` } : {}, body }
      );
      if (!response.ok) {
        throw new Error('Could not save picture');
      }
      const user = await response.json();
      dispatch({
        type: 'SET_USER',
        payload: { ...state.user, picture: user.profile_picture_url },
      });
    } catch (error) {
      setPictureError('Could not save your picture. Try again.');
    } finally {
      setPictureBusy(false);
    }
  };

  const handleLogout = async () => {
    // Clear token from localStorage
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_id');
    
    // Update app state
    dispatch({ type: 'LOGOUT' });
    
    // Optionally redirect to home page
    await clearPrivateOfflineData();
    window.location.href = '/';
  };

  if (!state.user) {
    return null;
  }

  return (
    <div className={`user-profile ${showFullProfile ? 'full-profile' : 'compact-profile'}`}>
      {showFullProfile ? (
        <div className="full-profile-content">
          <div className="profile-header">
            <div className="profile-avatar">
              {state.user.picture ? (
                <img src={state.user.picture} alt="Profile" className="avatar-image" />
              ) : (
                <div className="avatar-placeholder">
                  {state.user.name?.charAt(0) || state.user.email?.charAt(0) || 'U'}
                </div>
              )}
            </div>
            <div className="profile-info">
              <h3 className="profile-name">{state.user.name || 'User'}</h3>
              <p className="profile-email">{state.user.email}</p>
            </div>
          </div>
          <div className="profile-actions">
            <label className="profile-picture-action">
              {pictureBusy ? 'Saving picture…' : 'Change picture'}
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                disabled={pictureBusy}
                onChange={(event) => void uploadPicture(event.target.files?.[0])}
              />
            </label>
            {pictureError && <p className="profile-picture-error">{pictureError}</p>}
            <button onClick={handleLogout} className="logout-button">
              Sign out
            </button>
          </div>
        </div>
      ) : (
        <div className="compact-profile-content" ref={compactProfileRef}>
          <button
            ref={menuButtonRef}
            className="profile-avatar-small"
            onClick={() => setMenuOpen((open) => !open)}
            aria-expanded={menuOpen}
            aria-controls="account-menu-panel"
            aria-haspopup="dialog"
            aria-label="Account menu"
          >
            {state.user.picture ? (
              <img src={state.user.picture} alt="" className="avatar-image-small" />
            ) : (
              <span className="avatar-placeholder-small">
                {state.user.name?.charAt(0) || state.user.email?.charAt(0) || 'U'}
              </span>
            )}
          </button>
          {menuOpen && (
            <div
              id="account-menu-panel"
              className="profile-dropdown"
              role="dialog"
              aria-label="Account menu"
            >
              <div className="dropdown-content">
                <div className="dropdown-header">
                  <p className="dropdown-name">{state.user.name || 'User'}</p>
                  <p className="dropdown-email">{state.user.email}</p>
                </div>
                <hr className="dropdown-divider" />
                <Link
                  ref={firstMenuActionRef}
                  to="/account"
                  className="dropdown-account-link"
                  onClick={() => setMenuOpen(false)}
                >
                  Account &amp; family
                </Link>
                <Link
                  to="/voice"
                  className="dropdown-account-link"
                  onClick={() => setMenuOpen(false)}
                >
                  Family voices
                </Link>
                {state.user.is_internal_operator && (
                  <div className="dropdown-operator-tools" aria-label="Operator tools">
                    <span>Operator tools</span>
                    <Link to="/admin/library" onClick={() => setMenuOpen(false)}>Library editorial</Link>
                    <Link to="/admin/soundbeds" onClick={() => setMenuOpen(false)}>Soundbeds</Link>
                  </div>
                )}
                <label className="dropdown-picture-action">
                  {pictureBusy ? 'Saving picture…' : 'Change picture'}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    disabled={pictureBusy}
                    onChange={(event) => void uploadPicture(event.target.files?.[0])}
                  />
                </label>
                {pictureError && <p className="profile-picture-error">{pictureError}</p>}
                <button onClick={handleLogout} className="dropdown-logout">
                  Sign out
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UserProfile;
